import {Action} from '@reduxjs/toolkit'
import {PositionModel} from '../models/PositionModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  PositionsRequested: '[Request Positions] Action',
  PositionsLoaded: '[Load Positions] Positions API',
  PositionsFailed: '[Load Positions] Positions API Failed',
  PositionRequested: '[Request Position] Action',
  PositionLoaded: '[Load Position] Position API',
  PositionFailed: '[Load Position] Position API Failed',
  PositionCreated: '[Create Position] Action',
  PositionUpdated: '[Update Position] Action',
  PositionDeleted: '[Delete Position] Action',
}

const initialPositionState: IPositionState = {
  positions: [],
  position: undefined,
  loading: false,
  error: undefined,
}

export interface IPositionState {
  positions: PositionModel[]
  position?: PositionModel
  loading: boolean
  error?: string
}

export const reducer = (state: IPositionState = initialPositionState, action: ActionWithPayload<IPositionState>) => {
  switch (action.type) {
    case actionTypes.PositionsRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.PositionsLoaded: {
      const positions = action.payload?.positions || []
      return {...state, positions, loading: false, error: undefined}
    }

    case actionTypes.PositionsFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.PositionRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.PositionLoaded: {
      const position = action.payload?.position
      return {...state, position, loading: false, error: undefined}
    }

    case actionTypes.PositionFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.PositionCreated: {
      const position = action.payload?.position
      if (position) {
        return {...state, positions: [...state.positions, position], loading: false}
      }
      return state
    }

    case actionTypes.PositionUpdated: {
      const position = action.payload?.position
      if (position) {
        const updatedPositions = state.positions.map(p => (p.id === position.id ? position : p))
        return {...state, positions: updatedPositions, position, loading: false}
      }
      return state
    }

    case actionTypes.PositionDeleted: {
      const positionId = action.payload?.position?.id
      if (positionId) {
        const filteredPositions = state.positions.filter(p => p.id !== positionId)
        return {...state, positions: filteredPositions, loading: false}
      }
      return state
    }

    default:
      return state
  }
}

export const actions = {
  requestPositions: () => ({
    type: actionTypes.PositionsRequested,
  }),
  fulfillPositions: (positions: PositionModel[]) => ({
    type: actionTypes.PositionsLoaded,
    payload: {positions},
  }),
  failPositions: (error: string) => ({
    type: actionTypes.PositionsFailed,
    payload: {error},
  }),
  requestPosition: () => ({
    type: actionTypes.PositionRequested,
  }),
  fulfillPosition: (position: PositionModel) => ({
    type: actionTypes.PositionLoaded,
    payload: {position},
  }),
  failPosition: (error: string) => ({
    type: actionTypes.PositionFailed,
    payload: {error},
  }),
  createPosition: (position: PositionModel) => ({
    type: actionTypes.PositionCreated,
    payload: {position},
  }),
  updatePosition: (position: PositionModel) => ({
    type: actionTypes.PositionUpdated,
    payload: {position},
  }),
  deletePosition: (position: PositionModel) => ({
    type: actionTypes.PositionDeleted,
    payload: {position},
  }),
}
